import React, { useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, ChevronRight, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import { ASIDE_BTN, GHOST_BTN, ICON_BTN, INPUT_CLS, PRIMARY_BTN } from '../shared/listPage';

/**
 * Οδηγός εγκατάστασης αποθήκης (14/10/2026: νέα εμφάνιση, ίδια βήματα και ίδια αιτήματα): καλωσόρισμα →
 * προμηθευτής (υπάρχων ή νέος: POST /suppliers) → πρώτες ύλες (POST /raw-materials ανά γραμμή με όνομα) →
 * έτοιμο. Μέσα στην Επισκόπηση της Αποθήκης, σε ένα απλό πλαίσιο — χωρίς σκιά, χωρίς χρωματιστά εικονίδια.
 */

interface WizardSupplier {
  id?: number;
  name: string;
  phone: string;
  contact_person: string;
}

interface WizardMaterial {
  key: string; // local key for list management
  name: string;
  purchase_unit: 'ΚΙΛΑ' | 'ΤΕΜΑΧΙΑ' | 'ΛΙΤΡΑ' | 'ΓΡΑΜΜΑΡΙΑ';
  initial_stock: string;
  min_stock_level: string;
  purchase_price: string;
  supplier_id: number | '';
}

interface WarehouseSetupWizardProps {
  onComplete: () => void;
  onDismiss: () => void;
}

const UNIT_OPTIONS: WizardMaterial['purchase_unit'][] = ['ΚΙΛΑ', 'ΤΕΜΑΧΙΑ', 'ΛΙΤΡΑ', 'ΓΡΑΜΜΑΡΙΑ'];
const STEP_COUNT = 2; // Suppliers, Raw Materials

const emptyMaterial = (): WizardMaterial => ({
  key: Math.random().toString(36).slice(2),
  name: '',
  purchase_unit: 'ΚΙΛΑ',
  initial_stock: '',
  min_stock_level: '',
  purchase_price: '',
  supplier_id: '',
});

const CELL_CLS = 'w-full min-h-[40px] px-3 py-2 border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 text-sm '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring';
// Γραμμή πρώτης ύλης: όνομα | μονάδα | αρχικό | ελάχιστο | τιμή | αφαίρεση (στο κινητό δύο γραμμές).
const MATERIAL_GRID = 'grid grid-cols-2 sm:grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_2.5rem] gap-2 items-center';

const WarehouseSetupWizard: React.FC<WarehouseSetupWizardProps> = ({ onComplete, onDismiss }) => {
  const { t } = useTranslation(['warehouse', 'common']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0); // 0=welcome, 1=suppliers, 2=materials, 3=done
  const [saving, setSaving] = useState(false);

  // Step 1: Suppliers — pick exactly one
  const [existingSuppliers, setExistingSuppliers] = useState<{ id: number; name: string; contact_person?: string; phone?: string }[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  // null = none selected, -1 = "add new" selected, >0 = existing supplier id
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [newSupplier, setNewSupplier] = useState<WizardSupplier>({ name: '', phone: '', contact_person: '' });
  const [savedSuppliers, setSavedSuppliers] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (!store?.id) return;
    setLoadingExisting(true);
    const token = localStorage.getItem('xentrix_token');
    fetch(`${API_BASE_URL}/api/warehouse/stores/${store.id}/suppliers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { suppliers: [] })
      .then(d => {
        const list = d.suppliers || [];
        setExistingSuppliers(list);
        // Pre-select first existing supplier
        if (list.length > 0) setSelectedSupplierId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  }, [store?.id]);

  // Step 2: Materials
  const [materials, setMaterials] = useState<WizardMaterial[]>([emptyMaterial()]);

  // ─── Step 1 helpers ───────────────────────────────────────────────────────

  const canProceedStep1 =
    (selectedSupplierId !== null && selectedSupplierId !== -1) ||
    (selectedSupplierId === -1 && newSupplier.name.trim() !== '');

  const saveSuppliers = async () => {
    if (selectedSupplierId !== null && selectedSupplierId !== -1) {
      // Existing supplier selected — no API call needed
      const s = existingSuppliers.find(s => s.id === selectedSupplierId)!;
      setSavedSuppliers([{ id: s.id, name: s.name }]);
      setStep(2);
      return;
    }
    // New supplier to create
    setSaving(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const res = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store!.id}/suppliers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newSupplier.name.trim(),
          phone: newSupplier.phone.trim(),
          contact_person: newSupplier.contact_person.trim(),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedSuppliers([{ id: data.supplier?.id || data.id, name: newSupplier.name.trim() }]);
        setStep(2);
      } else {
        showToast('error', t('warehouse:supplierSaveError'));
      }
    } catch {
      showToast('error', t('warehouse:supplierSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Step 2 helpers ───────────────────────────────────────────────────────

  // Auto-assign the supplier to all rows when entering step 2
  useEffect(() => {
    if (step === 2 && savedSuppliers.length === 1) {
      setMaterials(prev => prev.map(m => ({ ...m, supplier_id: savedSuppliers[0].id })));
    }
  }, [step, savedSuppliers]);

  const updateMaterial = (key: string, field: keyof WizardMaterial, value: string | number) => {
    setMaterials(prev => prev.map(m => m.key === key ? { ...m, [field]: value } : m));
  };

  const addMaterialRow = () => setMaterials(prev => [
    ...prev,
    { ...emptyMaterial(), supplier_id: savedSuppliers[0]?.id ?? '' },
  ]);
  const removeMaterialRow = (key: string) => setMaterials(prev => prev.filter(m => m.key !== key));

  const saveMaterials = async () => {
    const toSave = materials.filter(m => m.name.trim());
    if (toSave.length === 0) { setStep(3); return; }
    setSaving(true);
    let saved = 0;
    try {
      const token = localStorage.getItem('xentrix_token');
      for (const m of toSave) {
        const res = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store!.id}/raw-materials`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: m.name.trim(),
            purchase_unit: m.purchase_unit,
            purchase_price: parseFloat(m.purchase_price) || 0,
            initial_stock: parseFloat(m.initial_stock) || 0,
            min_stock_level: parseFloat(m.min_stock_level) || 0,
            supplier_id: m.supplier_id || null,
          }),
        });
        if (res.ok) saved++;
      }
      showToast('success', t('warehouse:rawMaterialsSaved', { count: saved }));
      setStep(3);
    } catch {
      showToast('error', t('warehouse:materialsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const stepLabels = [t('warehouse:ui.wizard.stepSuppliers'), t('warehouse:ui.wizard.stepMaterials')];
  const titleId = `${uid}-title`;

  const next = (onClick: () => void, disabled: boolean) => (
    <button type="button" onClick={onClick} disabled={saving || disabled} className={PRIMARY_BTN} aria-busy={saving || undefined}>
      {saving
        ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('common:saving')}</>
        : <>{t('warehouse:nextStep')} <ChevronRight className="w-4 h-4" aria-hidden="true" /></>}
    </button>
  );

  return (
    <section className="rounded-2xl border border-line-strong" aria-labelledby={titleId} data-testid="warehouse-wizard">
      {/* Title bar */}
      <header className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 border-b border-line">
        <div className="min-w-0">
          <h2 id={titleId} className="text-base font-semibold text-t1">{t('warehouse:ui.wizard.title')}</h2>
          {step > 0 && step <= STEP_COUNT && (
            // Πρόοδος ως κείμενο: «Βήμα 1 από 2 · Προμηθευτής › Πρώτες ύλες», το τρέχον έντονο.
            <p className="mt-0.5 text-xs text-t3">
              {t('warehouse:stepOfTotal', { step, total: STEP_COUNT })}
              {' · '}
              {stepLabels.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span aria-hidden="true"> › </span>}
                  <span className={step === i + 1 ? 'font-semibold text-t1' : step > i + 1 ? 'text-t2' : ''}>
                    {step > i + 1 && <Check className="inline w-3 h-3 mr-0.5 -mt-0.5" aria-hidden="true" />}
                    {label}
                  </span>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
        <button type="button" onClick={onDismiss} className={ICON_BTN} aria-label={t('warehouse:ui.wizard.close')} title={t('warehouse:ui.wizard.close')}>
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </header>

      {/* ── Step 0: Welcome ── */}
      {step === 0 && (
        <div className="px-5 sm:px-6 py-6">
          <h3 className="text-lg font-semibold text-t1">{t('warehouse:warehouseSetup')}</h3>
          <p className="mt-1 max-w-xl text-sm text-t3 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('warehouse:warehouseSetupDesc') }}
          />
          <ol className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm text-t2">
            <li>{t('warehouse:step1Suppliers')}</li>
            <li aria-hidden="true" className="hidden sm:block text-t4">›</li>
            <li>{t('warehouse:step2RawMaterials')}</li>
            <li aria-hidden="true" className="hidden sm:block text-t4">›</li>
            <li>{t('warehouse:ready')}</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-2">
            <button type="button" onClick={() => setStep(1)} className={PRIMARY_BTN}>
              {t('warehouse:letsStart')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={onDismiss} className={GHOST_BTN}>
              {t('warehouse:doLater')}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 1: Suppliers ── */}
      {step === 1 && (
        <div className="px-5 sm:px-6 py-5">
          <p className="text-sm text-t3 mb-4">
            {t('warehouse:selectSupplierForSetup')}
          </p>

          {loadingExisting ? (
            <p className="flex items-center gap-2 text-t3 text-sm mb-4" role="status">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('common:loading')}
            </p>
          ) : (
            <div className="mb-5" role="radiogroup" aria-label={t('warehouse:ui.wizard.stepSuppliers')}>
              <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
                {/* Existing suppliers as radio options */}
                {existingSuppliers.map(s => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${selectedSupplierId === s.id ? 'bg-brand-soft' : 'hover:bg-surface-2'}`}
                  >
                    <input
                      type="radio"
                      name={`${uid}-supplier`}
                      checked={selectedSupplierId === s.id}
                      onChange={() => setSelectedSupplierId(s.id)}
                      className="w-4 h-4 shrink-0 accent-[hsl(var(--brand))]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-t1">{s.name}</span>
                      {(s.contact_person || s.phone) && (
                        <span className="ml-2 text-xs text-t3">
                          {[s.contact_person, s.phone].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                  </label>
                ))}

                {/* "Add new" radio option */}
                <label className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${selectedSupplierId === -1 ? 'bg-brand-soft' : 'hover:bg-surface-2'}`}>
                  <input
                    type="radio"
                    name={`${uid}-supplier`}
                    checked={selectedSupplierId === -1}
                    onChange={() => setSelectedSupplierId(-1)}
                    className="w-4 h-4 shrink-0 accent-[hsl(var(--brand))]"
                  />
                  <Plus className="w-4 h-4 shrink-0 text-t3" aria-hidden="true" />
                  <span className="text-sm font-medium text-t1">{t('warehouse:newSupplier')}</span>
                </label>
              </div>

              {/* New supplier form — shown only when "new" is selected */}
              {selectedSupplierId === -1 && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-12 gap-2">
                  <input
                    type="text"
                    value={newSupplier.name}
                    onChange={e => setNewSupplier(p => ({ ...p, name: e.target.value }))}
                    placeholder={t('warehouse:supplierNameRequired')}
                    aria-label={t('warehouse:supplierNameRequired')}
                    autoFocus
                    className={`${INPUT_CLS} sm:col-span-5`}
                  />
                  <input
                    type="text"
                    value={newSupplier.contact_person}
                    onChange={e => setNewSupplier(p => ({ ...p, contact_person: e.target.value }))}
                    placeholder={t('warehouse:responsible')}
                    aria-label={t('warehouse:responsible')}
                    className={`${INPUT_CLS} sm:col-span-4`}
                  />
                  <input
                    type="text"
                    value={newSupplier.phone}
                    onChange={e => setNewSupplier(p => ({ ...p, phone: e.target.value }))}
                    placeholder={t('common:phone')}
                    aria-label={t('common:phone')}
                    className={`${INPUT_CLS} sm:col-span-3`}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-between">
            <button type="button" onClick={() => setStep(0)} className={GHOST_BTN}>
              {t('warehouse:backButton')}
            </button>
            {next(saveSuppliers, !canProceedStep1)}
          </div>
        </div>
      )}

      {/* ── Step 2: Raw Materials ── */}
      {step === 2 && (
        <div className="px-5 sm:px-6 py-5">
          <p className="text-sm text-t3 mb-3">
            {t('warehouse:addRawMaterialsDesc')}
          </p>

          {/* Supplier badge */}
          {savedSuppliers[0] && (
            <p className="mb-4 text-xs text-t3" dangerouslySetInnerHTML={{ __html: t('warehouse:supplierAssigned', { name: savedSuppliers[0].name }) }} />
          )}

          {/* Column headers */}
          <div aria-hidden="true" className={`${MATERIAL_GRID} hidden sm:grid mb-1.5 px-0.5 text-xs font-semibold text-t3`}>
            <div>{t('warehouse:nameColumnRequired')}</div>
            <div>{t('warehouse:unitColumnHeader')}</div>
            <div>{t('warehouse:initialStockColumn')}</div>
            <div>{t('warehouse:minColumn')}</div>
            <div>{t('warehouse:pricePerUnitColumn')}</div>
            <div />
          </div>

          <ul className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1" data-testid="wizard-materials">
            {materials.map((m, index) => (
              <li key={m.key} className={MATERIAL_GRID}>
                <input
                  type="text"
                  value={m.name}
                  onChange={e => updateMaterial(m.key, 'name', e.target.value)}
                  placeholder={t('warehouse:materialPlaceholder')}
                  aria-label={t('warehouse:ui.wizard.rowName', { index: index + 1 })}
                  className={`${CELL_CLS} col-span-2 sm:col-span-1`}
                />
                <select
                  value={m.purchase_unit}
                  onChange={e => updateMaterial(m.key, 'purchase_unit', e.target.value)}
                  aria-label={t('warehouse:ui.wizard.rowUnit', { index: index + 1 })}
                  className={`${CELL_CLS} pr-8`}
                >
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  value={m.initial_stock}
                  onChange={e => updateMaterial(m.key, 'initial_stock', e.target.value)}
                  placeholder="0"
                  aria-label={t('warehouse:ui.wizard.rowInitial', { index: index + 1 })}
                  className={`${CELL_CLS} text-right tabular-nums`}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={m.min_stock_level}
                  onChange={e => updateMaterial(m.key, 'min_stock_level', e.target.value)}
                  placeholder="0"
                  aria-label={t('warehouse:ui.wizard.rowMin', { index: index + 1 })}
                  className={`${CELL_CLS} text-right tabular-nums`}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={m.purchase_price}
                  onChange={e => updateMaterial(m.key, 'purchase_price', e.target.value)}
                  placeholder="0.00"
                  aria-label={t('warehouse:ui.wizard.rowPrice', { index: index + 1 })}
                  className={`${CELL_CLS} text-right tabular-nums`}
                />
                {materials.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeMaterialRow(m.key)}
                    aria-label={t('warehouse:ui.wizard.removeRow', { index: index + 1 })}
                    title={t('warehouse:ui.wizard.removeRow', { index: index + 1 })}
                    className={`${ICON_BTN} hover:text-red-600 dark:hover:text-red-400`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                ) : <span aria-hidden="true" />}
              </li>
            ))}
          </ul>

          <button type="button" onClick={addMaterialRow} className={`${ASIDE_BTN} mb-6`}>
            <Plus className="w-4 h-4" aria-hidden="true" /> {t('warehouse:ui.wizard.addRow')}
          </button>

          <div className="flex flex-wrap gap-2 justify-between">
            <button type="button" onClick={() => setStep(1)} className={GHOST_BTN}>
              {t('warehouse:backButton')}
            </button>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setStep(3)} className={GHOST_BTN}>
                {t('warehouse:skip')}
              </button>
              {next(saveMaterials, !materials.some(m => m.name.trim()))}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <div className="px-5 sm:px-6 py-6">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-t1">
            <Check className="w-5 h-5 text-emerald-700 dark:text-emerald-400" aria-hidden="true" />
            {t('warehouse:warehouseReady')}
          </h3>
          <p className="mt-1 max-w-xl text-sm text-t3 leading-relaxed">
            {t('warehouse:warehouseReadyDesc')}
          </p>
          <dl className="mt-4 max-w-2xl divide-y divide-line border-y border-line">
            {[
              [t('warehouse:purchaseRecordingTitle'), t('warehouse:purchaseRecordingDesc')],
              [t('warehouse:stockCountTitle'), t('warehouse:stockCountDesc')],
              [t('warehouse:linkProducts'), t('warehouse:linkProductsDesc')],
            ].map(([title, desc]) => (
              <div key={title} className="py-3 sm:grid sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4">
                <dt className="text-sm font-medium text-t1">{title}</dt>
                <dd className="mt-0.5 sm:mt-0 text-sm text-t3">{desc}</dd>
              </div>
            ))}
          </dl>
          <button type="button" onClick={onComplete} className={`${PRIMARY_BTN} mt-6`}>
            {t('warehouse:goToWarehouse')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
};

export default WarehouseSetupWizard;
